<script setup lang="ts">
  import { CartLayout, CartWithSummaryLayout } from '@storefront/components';
  import { type FormContextInterface, setModelWithContext, useHttpClient, useRoute } from '@othercommerce/foundation';
  import { useVerifiedCart, addShippingInfoEvent, selectContentEvent, useGoogleTagManager } from '@storefront/storefront';
  import { computed, ref } from 'vue';
  import type { ResolvedShippingOption } from '@storefront/resources';
  import { ShippingType } from '@othercommerce/domain';

  defineOptions({
    layout: [CartLayout, CartWithSummaryLayout],
    inheritAttrs: false,
  });

  interface Props {
    options: ResolvedShippingOption[];
  }

  const props = defineProps<Props>();
  const cart = useVerifiedCart();
  const http = useHttpClient();
  const gtm = useGoogleTagManager();
  const route = useRoute();

  const selected = ref(props.options.find(option => option.id === cart.value.shippingOptionId));

  const collection = computed(() => {
    let option = selected.value;

    if (option) {
      if (option.types.includes(ShippingType.COLLECTION)) {
        return true;
      }
    }

    return false;
  });

  const pickup = computed(() => {
    let option = selected.value;

    if (option) {
      if (option.types.includes(ShippingType.PARCEL_LOCKER) || option.types.includes(ShippingType.SERVICE_POINT)) {
        return true;
      }
    }

    return false;
  });

  function onChoose(option: ResolvedShippingOption, ctx: FormContextInterface) {
    setModelWithContext('shippingOptionId', ctx, option.id);
    selected.value = option;
    gtm.push(selectContentEvent('shippingOption', option.name));
  }

  function onSubmit(data: any) {
    // Send only required data, otherwhise request can be too big
    const { items, ...payload } = data;

    return http.patch(route('web.cart.shipping.option.update', payload)).then(() => {
      gtm.push(addShippingInfoEvent(cart.value));
    });
  }
</script>
<template>
  <FormController :data="cart" v-slot="{ data, processing, ctx }" @submit="onSubmit">
    <div class="shipping-method-form">
      <SfListCard v-for="option in options" :key="option.id" class="shipping-method-form__option" role="button" tabindex="0" interactive :selected="data.shippingOptionId === option.id" @click="onChoose(option, ctx)" @keydown.space="onChoose(option, ctx)">
        <template #image>
          <SfIconBox class="shipping-method-form__icon" aria-hidden="true" />
        </template>
        <template #default>
          <header class="shipping-method-form__header">
            <h2 class="shipping-method-form__heading">{{ option.name }}</h2>
            <p class="shipping-method-form__description">{{ option.description }}</p>
          </header>
        </template>
        <template #actions>
          <div class="shipping-method-form__details">
            <div class="shipping-method-form__price">
              <SfMoney :value="option.cost" :code="cart.currency.code" :decimals="cart.currency.decimals" class="shipping-method-form__money" />
            </div>
            <div class="shipping-method-form__time">
              {{ option.shippingTime }}
            </div>
          </div>
        </template>
      </SfListCard>
    </div>
    <div v-if="pickup && selected && selected.mapDriverServiceOriginal && selected.pickupPointMap" class="pickup-point-form">
      <ShippingMapWidget :service="selected.mapDriverServiceOriginal" :types="selected.types" :pickupPointMap="selected.pickupPointMap" />
    </div>
    <div v-if="collection" class="pickup-point-form">
      <ShippingCollect />
    </div>
    <UiButtonGroup>
      <SfButton primary :loading="processing" type="submit">
        {{ $t('SHOP.CART.ACTION.CONTINUE') }}
      </SfButton>
    </UiButtonGroup>
  </FormController>

  <RouterView />
</template>
<style lang="scss">
  @use "@storefront/styles/mixins/media";

  .shipping-method-form {
    display: flex;
    flex-direction: column;
    gap: var(--form-gap-y) var(--form-gap-x);

    &__option {
      cursor: pointer;
    }

    &__icon {
      font-size: 2rem;
    }

    &__heading {
      font-size: 1.25rem;
    }

    &__description {
      font-size: 0.875rem;
      color: var(--sf-list-card-color-alt);
    }

    &__details {
      display: flex;
      flex-direction: column;
      align-items: flex-end;
    }

    &__price {
      font-weight: var(--font-weight-semi);
      font-size: 1.25rem;
    }

    &__time {
      font-size: 0.875rem;
      color: var(--sf-list-card-color-alt);
    }
  }

  @include media.query(lg) {
    .shipping-method-form {
      &__price, &__heading {
        font-size: 1rem;
      }

      &__description {
        font-size: 0.875rem;
      }

      &__icon {
        font-size: 1.5rem;
      }
    }
  }
</style>
